/**
 * @file DropdownMenu 下拉菜单
 * @description 操作菜单；支持分割线；fade；视口边界；列表 ScrollArea。
 *
 * @whenToUse 表格行操作、更多按钮。
 * @whenNotToUse 表单枚举选择（用 Select）；悬停说明（用 Tooltip）；常驻导航（用 Menu）。
 */

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/cn.ts'
import {
  computeDropdownBox,
  floatingPanelStyle,
  type FloatingBox,
} from '@/lib/floating.ts'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'

export type DropdownMenuItem = {
  key: string
  /** item：可点击项；divider：分割线（忽略 label） */
  type?: 'item' | 'divider'
  label?: ReactNode
  disabled?: boolean
  danger?: boolean
  onSelect?: () => void
}

export type DropdownMenuProps = {
  /** 触发器（通常是 Button） */
  trigger: ReactNode
  items: DropdownMenuItem[]
  align?: 'start' | 'end'
}

/** 菜单项高度（h-8） */
const ITEM_HEIGHT = 32
/** 分割线高度（my-1 + h-px） */
const DIVIDER_HEIGHT = 9
/** 列表上下内边距（p-1） */
const PANEL_PAD_Y = 8

/**
 * 估算菜单内容高度（含分割线与内边距）
 * @param items 菜单项
 */
function estimateMenuHeight(items: DropdownMenuItem[]): number {
  return items.reduce(
    (sum, item) =>
      sum + (item.type === 'divider' ? DIVIDER_HEIGHT : ITEM_HEIGHT),
    PANEL_PAD_Y,
  )
}

export function DropdownMenu({
  trigger,
  items,
  align = 'start',
}: DropdownMenuProps) {
  const triggerRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [box, setBox] = useState<FloatingBox | null>(null)

  const updateBox = () => {
    const el = triggerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const next = computeDropdownBox(rect, 4, 160)
    if (align === 'end') {
      next.left = Math.max(8, rect.right - next.width)
    }
    setBox(next)
  }

  useEffect(() => {
    if (open) {
      setMounted(true)
      updateBox()
      const id = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(id)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onWin = () => updateBox()
    const onPointer = (event: MouseEvent) => {
      const t = event.target as Node
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <>
      <div
        ref={triggerRef}
        className="inline-flex"
        onClick={() => setOpen((v) => !v)}
      >
        {trigger}
      </div>
      {mounted && box && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              role="menu"
              className={cn(
                'fixed z-50 overflow-hidden rounded-lg border border-border bg-raised transition-opacity duration-200',
                visible ? 'opacity-100' : 'opacity-0',
              )}
              style={floatingPanelStyle(box)}
            >
              <ScrollArea
                className="w-full"
                style={{
                  height: Math.min(box.maxHeight, estimateMenuHeight(items)),
                }}
              >
                <div className="p-1">
                  {items.map((item) =>
                    item.type === 'divider' ? (
                      <div
                        key={item.key}
                        role="separator"
                        className="my-1 h-px bg-border"
                      />
                    ) : (
                      <button
                        key={item.key}
                        type="button"
                        role="menuitem"
                        disabled={item.disabled}
                        className={cn(
                          'flex h-8 w-full items-center rounded-md px-2 text-left text-sm transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50',
                          item.danger
                            ? 'text-danger hover:bg-danger/10 hover:text-danger'
                            : 'text-foreground hover:bg-accent/25 hover:text-primary',
                        )}
                        onClick={() => {
                          if (item.disabled) return
                          item.onSelect?.()
                          setOpen(false)
                        }}
                      >
                        {item.label}
                      </button>
                    ),
                  )}
                </div>
              </ScrollArea>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
